import { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  limit,
  where,
  Timestamp
} from 'firebase/firestore';
import { db, getActiveAuth } from '../config/firebase';
import { collaboratorMessagingService } from '../services/collaboratorMessagingService';
import type { CollaboratorConversation, CollaboratorMessage } from '../services/collaboratorMessagingService';

/**
 * Helper to convert Firestore timestamps to ISO strings
 */
const convertTimestamp = (value: any): string | undefined => {
  if (!value) return undefined;
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  if (value.toDate) return value.toDate().toISOString();
  return undefined;
};

/**
 * Normalize conversation payloads from Firestore or REST API responses
 * to the `CollaboratorConversation` shape expected by the UI.
 */
const normalizeConversation = (
  data: any,
  docId?: string,
  currentUserId?: string
): CollaboratorConversation => {
  const participantIds = data.participant_ids || data.participantIds || [];
  const participantNames = data.participant_names || data.participantNames || {};
  const participantRoles = data.participant_roles || data.participantRoles || {};
  const unreadCounts = data.unread_counts || data.unreadCounts || {};

  const otherParticipantId =
    data.other_participant_id ||
    data.otherParticipantId ||
    participantIds.find((id: string) => id !== currentUserId);
  const otherParticipantName =
    data.other_participant_name ||
    data.otherParticipantName ||
    (otherParticipantId ? participantNames[otherParticipantId] : 'Unknown');
  const otherParticipantRole =
    data.other_participant_role ||
    data.otherParticipantRole ||
    (otherParticipantId ? participantRoles[otherParticipantId] : undefined);

  const computedUnreadCount =
    data.unread_count ??
    data.unreadCount ??
    (currentUserId ? unreadCounts?.[currentUserId] || 0 : 0);

  const normalized: CollaboratorConversation = {
    id: docId || data.id,
    profileId: data.profile_id || data.profileId,
    profileName: data.profile_name || data.profileName,
    participantIds,
    participantNames,
    participantRoles,
    unreadCounts,
    lastMessage: data.last_message || data.lastMessage,
    lastMessageAt: convertTimestamp(data.last_message_at ?? data.lastMessageAt),
    lastSenderId: data.last_sender_id || data.lastSenderId,
    createdAt: convertTimestamp(data.created_at ?? data.createdAt) || '',
    updatedAt: convertTimestamp(data.updated_at ?? data.updatedAt) || '',
    status: data.status || 'active',
    requiresAcceptance: data.requires_acceptance ?? data.requiresAcceptance,
    initiatorId: data.initiator_id ?? data.initiatorId,
    initiatorName: data.initiator_name ?? data.initiatorName,
    otherParticipantId,
    otherParticipantName,
    otherParticipantInitials: (data.other_participant_initials || data.otherParticipantInitials) ??
      (otherParticipantName
        ? otherParticipantName
            .split(' ')
            .map((n: string) => n[0])
            .slice(0, 2)
            .join('')
            .toUpperCase()
        : undefined),
    otherParticipantRole,
    unreadCount: computedUnreadCount
  };

  return normalized;
};

/**
 * Hook to get all conversations for the current user (from their index)
 * 
 * This returns conversations from the user's collaborator_conversation_index,
 * which includes conversations across all profiles the user has access to.
 */
export const useCollaboratorInbox = () => {
  const [conversations, setConversations] = useState<CollaboratorConversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    const auth = getActiveAuth();
    const user = auth.currentUser;

    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      // Query user's conversation index
      const indexRef = collection(db, 'accounts', user.uid, 'collaborator_conversation_index');
      const q = query(indexRef, orderBy('updated_at', 'desc'));

      unsubscribe = onSnapshot(q, (snapshot) => {
        const convs: CollaboratorConversation[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          convs.push({
            id: doc.id,
            profileId: data.profile_id,
            profileName: data.profile_name,
            participantIds: data.participant_ids || [],
            participantNames: data.participant_names || {},
            participantRoles: data.participant_roles || {},
            unreadCounts: data.unread_counts || {},
            lastMessage: data.last_message,
            lastMessageAt: convertTimestamp(data.last_message_at),
            lastSenderId: data.last_sender_id,
            createdAt: convertTimestamp(data.created_at) || '',
            updatedAt: convertTimestamp(data.updated_at) || '',
            status: data.status || 'active',
            // Index-specific fields
            otherParticipantId: data.other_participant_id,
            otherParticipantName: data.other_participant_name,
            otherParticipantInitials: data.other_participant_initials,
            otherParticipantRole: data.other_participant_role,
            unreadCount: data.unread_count || 0
          });
        });
        setConversations(convs);
        setIsLoading(false);
      }, (err) => {
        console.error('Error fetching collaborator inbox:', err);
        setError(err.message);
        setIsLoading(false);
      });

    } catch (err: any) {
      console.error('Error setting up inbox listener:', err);
      setError(err.message);
      setIsLoading(false);
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return { conversations, isLoading, error };
};

/**
 * Hook to get conversations for a specific profile
 * 
 * Returns all conversations on the profile that the current user is a participant of.
 */
export const useCollaboratorConversations = (profileId?: string) => {
  const [conversations, setConversations] = useState<CollaboratorConversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usedFallback, setUsedFallback] = useState(false);

  useEffect(() => {
    // Reset fallback state when profile changes
    setUsedFallback(false);
    let unsubscribe: (() => void) | undefined;
    const auth = getActiveAuth();
    const user = auth.currentUser;

    if (!user || !profileId) {
      setConversations([]);
      setIsLoading(false);
      return;
    }

    const handlePermissionFallback = async () => {
      if (usedFallback) return;
      setUsedFallback(true);
      try {
        setIsLoading(true);
        const apiConversations = await collaboratorMessagingService.getProfileConversations(profileId);
        setConversations(
          apiConversations.map((conv) => normalizeConversation(conv, conv.id, user.uid))
        );
        setError(null);
      } catch (apiErr: any) {
        console.error('Fallback collaborator conversations fetch failed:', apiErr);
        setError(apiErr.message || 'Unable to load conversations');
      } finally {
        setIsLoading(false);
      }
    };

    try {
      // Query profile's collaborator_conversations where user is a participant
      const conversationsRef = collection(db, 'profiles', profileId, 'collaborator_conversations');
      const q = query(
        conversationsRef,
        where('participant_ids', 'array-contains', user.uid),
        orderBy('updated_at', 'desc')
      );

      unsubscribe = onSnapshot(q, (snapshot) => {
        const convs: CollaboratorConversation[] = [];
        snapshot.forEach((doc) => {
          convs.push(normalizeConversation(doc.data(), doc.id, user.uid));
        });
        setConversations(convs);
        setIsLoading(false);
      }, (err) => {
        console.error('Error fetching collaborator conversations:', err);
        setError(err.message);
        setIsLoading(false);
        // Graceful fallback to REST API if Firestore permissions block the listener (seen in prod)
        if (err.code === 'permission-denied') {
          void handlePermissionFallback();
        }
      });

    } catch (err: any) {
      console.error('Error setting up conversations listener:', err);
      setError(err.message);
      setIsLoading(false);
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [profileId]);

  return { conversations, isLoading, error };
};

/**
 * Hook to get messages for a specific conversation
 */
export const useCollaboratorMessages = (
  conversationId: string | undefined,
  profileId: string | undefined
) => {
  const [messages, setMessages] = useState<CollaboratorMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!conversationId || !profileId) {
      setMessages([]);
      return;
    }

    let unsubscribe: (() => void) | undefined;
    setIsLoading(true);

    try {
      const messagesRef = collection(
        db,
        'profiles',
        profileId,
        'collaborator_conversations',
        conversationId,
        'messages'
      );
      const q = query(messagesRef, orderBy('created_at', 'desc'), limit(50));

      unsubscribe = onSnapshot(q, (snapshot) => {
        const msgs: CollaboratorMessage[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          msgs.push({
            id: doc.id,
            conversationId: data.conversation_id,
            senderId: data.sender_id,
            senderRole: data.sender_role,
            senderName: data.sender_name,
            content: data.content,
            createdAt: convertTimestamp(data.created_at) || '',
            readAt: convertTimestamp(data.read_at),
            metadata: data.metadata
          });
        });
        // Reverse to show oldest first (chat UI expects chronological order)
        setMessages(msgs.reverse());
        setIsLoading(false);
      }, (err) => {
        console.error('Error fetching messages:', err);
        setError(err.message);
        setIsLoading(false);
      });

    } catch (err: any) {
      console.error('Error setting up message listener:', err);
      setError(err.message);
      setIsLoading(false);
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [conversationId, profileId]);

  return { messages, isLoading, error };
};

/**
 * Hook to check if a profile has any collaborators (for showing messaging in navbar)
 */
export const useHasCollaborators = (profileId?: string): boolean => {
  const [hasCollaborators, setHasCollaborators] = useState(false);
  const { conversations, isLoading } = useCollaboratorConversations(profileId);

  useEffect(() => {
    if (!profileId) {
      setHasCollaborators(false);
      return;
    }

    // If there are any conversations, the user has collaborators
    if (!isLoading && conversations.length > 0) {
      setHasCollaborators(true);
      return;
    }

    // Also check the profile's access collection for collaborators
    const checkCollaborators = async () => {
      try {
        const { profileAccessService } = await import('../services/profileAccessService');
        const collabs = await profileAccessService.getProfileCollaborators(profileId);
        const hasNonOwnerCollaborators = collabs.some(c => c.role !== 'owner');
        setHasCollaborators(hasNonOwnerCollaborators);
      } catch (error) {
        console.error('Error checking collaborators:', error);
        setHasCollaborators(false);
      }
    };

    if (!isLoading) {
      checkCollaborators();
    }
  }, [profileId, conversations.length, isLoading]);

  return hasCollaborators;
};

